import { MouseEvent } from 'react';
import { usePhrase } from '@/hooks';
import { Box, Typography } from '@mui/material';
import { Control, FieldErrorsImpl, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { RadioField } from '@/components/forms/inputs/RadioField/RadioField';
import { ILastPaymentData, IMethodsData } from '@/types/types';
import { WALLET_REQUEST_PHRASES } from '@/consts/phrases/walletPhrases';
import { walletFormModel } from '@/components/forms/forms/wallet/walletFormModel';
import { WalletPaymentSteps } from '@/types/enums/forms/wallet/walletPaymentSteps';
import { IWalletStartPaymentFormDetails } from '@/types/types';
import { isEmpty } from '@/utils/fp';

interface WalletPaymentMethodSelectorProps {
    control: Control;
    errors?: Partial<FieldErrorsImpl<IWalletStartPaymentFormDetails>>;
    methodsData: IMethodsData[];
    lastPayment?: ILastPaymentData;
    setValue: UseFormSetValue<Partial<IWalletStartPaymentFormDetails>>;
    getValues: UseFormGetValues<Partial<IWalletStartPaymentFormDetails>>;
    handleOpenRequestDialog: (val?: number, field?: keyof IWalletStartPaymentFormDetails) => void;
    paymentInfoExist?: boolean;
    currencySymbol?: string;
    currencyCode?: string;
    currencyPosition?: string;
}

import styles from './WalletPaymentMethodSelector.module.css';
import { WalletRadioCustomLabel } from '../WalletRadioCustomLabel/WalletRadioCustomLabel';

export const WalletPaymentMethodSelector = ({
    control,
    errors,
    methodsData,
    lastPayment,
    setValue,
    getValues,
    handleOpenRequestDialog,
    paymentInfoExist = false,
    currencySymbol,
    currencyCode,
    currencyPosition,
}: WalletPaymentMethodSelectorProps) => {
    const { t } = usePhrase();
    const {
        lastPaymentMethodId: lastPaymentMethodIdModel,
        paymentMethodId: paymentMethodIdModel,
        existingPayment: existingPaymentModel,
        lastPayment: lastPaymentModel,
    } = walletFormModel[WalletPaymentSteps.PaymentStart];

    const lastPaymentMethod = lastPayment
        ? methodsData.find((item) => item.paymentMethodId === lastPayment.paymentMethodId)
        : undefined;

    const lastMethod =
        lastPayment && lastPaymentMethod
            ? [
                  {
                      value: lastPayment.paymentMethodId,
                      label: lastPaymentMethod.phraseId,
                      customLabel: (
                          <WalletRadioCustomLabel
                              paymentMethodId={lastPayment.paymentMethodId}
                              phraseId={lastPaymentMethod.phraseId}
                              descriptionPhraseId={lastPaymentMethod.descriptionPhraseId}
                              completeDt={lastPayment.completeDt}
                              summary={lastPayment.summary}
                          />
                      ),
                  },
              ]
            : [];

    const primaryMethods = methodsData.map(
        ({
            descriptionPhraseId,
            phraseId,
            paymentMethodId,
            isComingSoon,
            amountMax,
            amountMin,
        }) => ({
            value: paymentMethodId,
            label: phraseId,
            disabled: isComingSoon,
            customLabel: (
                <WalletRadioCustomLabel
                    paymentMethodId={paymentMethodId}
                    phraseId={phraseId}
                    descriptionPhraseId={descriptionPhraseId}
                    isComingSoon={isComingSoon}
                    amountMax={amountMax}
                    amountMin={amountMin}
                    currencySymbol={currencySymbol}
                    currencyCode={currencyCode}
                    currencyPosition={currencyPosition}
                />
            ),
        }),
    );

    const handleLastPaymentChange = (evt: MouseEvent) => {
        const target = evt.target as HTMLInputElement;
        if (paymentInfoExist) {
            evt.preventDefault();
            handleOpenRequestDialog(parseInt(target.value), lastPaymentMethodIdModel.name);
        } else {
            setValue(paymentMethodIdModel.name, null);
            setValue(existingPaymentModel.name, false);
            setValue(lastPaymentModel.name, true);
        }
    };

    const handleNewPaymentChange = (evt: MouseEvent) => {
        const target = evt.target as HTMLInputElement;
        const currentVal = getValues(paymentMethodIdModel.name);
        if (String(target.value) === String(currentVal)) {
            evt.preventDefault();
            return;
        }
        if (paymentInfoExist) {
            evt.preventDefault();
            handleOpenRequestDialog(parseInt(target.value), paymentMethodIdModel.name);
        } else {
            setValue(lastPaymentMethodIdModel.name, null);
            setValue(lastPaymentModel.name, false);
        }
    };

    const lastPaymentExists = lastPayment && !isEmpty(lastPayment);

    return (
        <>
            {lastPaymentExists && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        {t(WALLET_REQUEST_PHRASES.REPEAT_TITLE)}
                    </Typography>
                    <RadioField
                        radioClassName={styles.radioGroup}
                        controlClassName={`radioGroupThemeControl ${styles.item} ${styles.itemFullWidth}`}
                        activeClassName={`radioGroupControlActive ${styles.activeItem}`}
                        control={control}
                        name={lastPaymentMethodIdModel.name}
                        data={lastMethod}
                        error={errors?.lastPaymentMethodId}
                        onClick={handleLastPaymentChange}
                        data-testid="existing-payment-method-id"
                    />
                </Box>
            )}
            <Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                    {lastPaymentExists && t(WALLET_REQUEST_PHRASES.OR_SELECT_TITLE)}{' '}
                    {t(WALLET_REQUEST_PHRASES.SELECT_PAYMENT_TITLE)}
                </Typography>

                <RadioField
                    radioClassName={styles.radioGroup}
                    controlClassName={`radioGroupThemeControl ${styles.item}`}
                    activeClassName={`radioGroupControlActive ${styles.activeItem}`}
                    control={control}
                    name={paymentMethodIdModel.name}
                    data={primaryMethods}
                    error={errors?.paymentMethodId}
                    onClick={handleNewPaymentChange}
                    data-testid="new-payment-method-id"
                />
            </Box>
        </>
    );
};
